/*
 * Copyright (C) 2026 Bohemian Miser <https://substack.com/@bohemianmiser>
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published
 * by the Free Software Foundation, either version 3 of the License, or
 * (at your option) any later version.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE.  See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program.  If not, see <https://www.gnu.org/licenses/>.
 */

'use client';

import React, { useCallback, useEffect, useMemo, useRef, useState, forwardRef, useImperativeHandle, memo } from 'react';
import ReactFlow, {
    Background,
    Controls,
    ReactFlowProvider,
    useReactFlow
} from 'reactflow';

// @ts-ignore
import { Node, Edge, Panel, MarkerType, useNodesState, useEdgesState } from 'reactflow';

type Node = any;
type Edge = any;
import 'reactflow/dist/style.css';
import { forceSimulation, forceLink, forceManyBody, forceCollide, forceY, forceX } from 'd3-force';

import { calculateLayout, LayoutMode } from '../../lib/recipe-lanes/layout';
import { calculateRepulsiveCurvesLayout } from '../../lib/recipe-lanes/layout-force';
import {
    calculateNotationLayout,
    reanchorNotationStations,
    NOTATION_STATION_TYPE,
} from '../../lib/recipe-lanes/layout-notation';
import { getLeafNodeIds } from '../../lib/recipe-lanes/leaf-nodes';
import { RecipeGraph } from '../../lib/recipe-lanes/types';
import { getNodeIconUrl, getNodeIconId, preserveNodeShortlist, getNodeShortlistLength } from '../../lib/recipe-lanes/model-utils';
import MinimalNode from './nodes/minimal-node';
import LaneNode from './nodes/lane-node';
import MicroNode from './nodes/micro-node';
import TimelineNode from './nodes/timeline-node';
import NotationVerbNode from './nodes/notation-verb-node';
import NotationStationNode from './nodes/notation-station-node';
import FloatingEdge from './edges/floating-edge';
import TimelineEdge from './edges/timeline-edge';
import NotationEdge from './edges/notation-edge';
import TimelineBackground, { type TimelineData } from './timeline-background';
import { getCanvasTheme } from '@/lib/recipe-lanes/canvas-theme';
import { track } from '@/lib/analytics';
import { toPng } from 'html-to-image';
import { Download, Share2, Undo, Redo, Check, Save, Copy, ListChecks } from 'lucide-react';
import { useSaveAndFork, getSaveButtonState } from './hooks/useSaveAndFork';
import { useAutosave } from './hooks/useAutosave';
import { useRecipeStore } from '../../lib/stores/recipe-store';

interface ReactFlowDiagramProps {
  graph: RecipeGraph;
  mode: LayoutMode | 'repulsive' | 'notation';
  spacing?: number;
  edgeStyle?: 'straight' | 'step' | 'bezier';
  textPos?: 'bottom' | 'top' | 'left' | 'right';
  isLive?: boolean;
  onInteraction?: () => void;
  onEdit?: () => void;
  onSave?: (newGraph: RecipeGraph) => void;
  isPublic?: boolean;
  onVisibilityChange?: (isPublic: boolean) => void;
  isLoggedIn?: boolean;
  onNotify?: (msg: string) => void;
  isOwner?: boolean; // YOLO: Added to support auto-save on move logic
  iconTheme?: 'classic' | 'modern' | 'modern_clean';
  // Analytics-only: which recipe (the ?id= search param) is being edited, so
  // diagram_interacted can be deduped per-recipe. Passed as a plain string
  // prop (not read via useSearchParams() in here) — this component is memoized
  // and Zustand-selector-optimized to minimize re-renders, and subscribing to
  // useSearchParams() directly would re-render it on every router navigation
  // (see the recipeId-vs-searchParams note in app/lanes/page.tsx).
  recipeId?: string;
}

export interface ReactFlowDiagramHandle {
    resetLayout: () => void;
    toggleVisibility: () => Promise<void>;
    toggleAnonymous: () => Promise<void>;
    getGraph: () => RecipeGraph;
}

const INITIAL_NODE_TYPES = {
    minimal: MinimalNode,
    lane: LaneNode,
    micro: MicroNode,
    'timeline-node': TimelineNode,
    'notation-verb': NotationVerbNode,
    'notation-station': NotationStationNode,
};

const INITIAL_EDGE_TYPES = {
    floating: FloatingEdge,
    timeline: TimelineEdge,
    notation: NotationEdge,
};

/**
 * Notation nodes always label BELOW the icon.
 *
 * The toolbar's text-position select is a global preference, but 'left'/'right'
 * widen a MinimalNode wrapper from 100/120px to 140/180px. The notation layout
 * spaces by the 100/120px contract (and the edge anchors derive from it), so
 * letting the preference through detaches every label and every edge in the
 * view. Other layout modes are unaffected.
 */
const NOTATION_TEXT_POS = 'bottom' as const;

/** fitView padding for every layout mode except notation. */
const FIT_PADDING = 0.1;
/**
 * fitView padding in notation mode.
 *
 * ReactFlow fits the bounding box of the *measured node boxes*. Notation draws
 * a node's label, its duration/temperature chips and its station badge label
 * OUTSIDE that box (absolutely positioned, see notation-metrics.ts), so the
 * real ink is wider and taller than the box ReactFlow measures — and a 0.1 fit
 * slices the outermost labels off the edge of the viewport. The extra padding
 * is the allowance for ink the fit cannot see.
 */
const NOTATION_FIT_PADDING = 0.15;
/** fitView zoom cap — small recipes should not fill the whole viewport. */
const FIT_MAX_ZOOM = 1.5;
/** fitView options for a given layout mode. */
function fitOptionsFor(mode: string) {
    return {
        padding: mode === 'notation' ? NOTATION_FIT_PADDING : FIT_PADDING,
        maxZoom: FIT_MAX_ZOOM,
    };
}

/**
 * The text position a node should actually render with. ONE function, used by
 * both the node-build spread and the textPos effect, so the notation pin cannot
 * half-apply (it did: the effect re-broadcast the raw preference onto nodes the
 * build had just pinned).
 */
function effectiveTextPos(
    mode: string,
    textPos: 'bottom' | 'top' | 'left' | 'right',
): 'bottom' | 'top' | 'left' | 'right' {
    return mode === 'notation' ? NOTATION_TEXT_POS : textPos;
}

/**
 * True for the render-only "spine stub" edge the notation layout draws from a
 * station badge to its row's first step.
 *
 * Reads the marker the LAYOUT set at the single place it mints that edge (see
 * NotationVisualEdge.synthetic), carried through into the RF edge's `data`.
 * Deriving it here instead — from an id prefix, or even from the source node's
 * type — is how this went wrong twice: recipe node ids are unconstrained
 * strings, so a real node called `notation-station-prep` matched, and its real
 * edges were dropped from the save.
 *
 * Every edge walker (ancestor selection, pivot drag, the physics sim, the save
 * path) must agree, or the "fixed" badge gets dragged/simulated away with
 * nothing to restore it.
 *
 * The two ancestor walks below are deliberately left inline in the component
 * rather than extracted into one function: hoisting them out changes what the
 * React Compiler lint can analyse in this file and surfaces two unrelated
 * pre-existing violations, which belong in their own PR.
 */
function isSyntheticStationEdge(edge: { data?: { synthetic?: boolean } }): boolean {
    return edge.data?.synthetic === true;
}

// Fire `diagram_interacted` at most once per recipe per page load, regardless
// of how many times the diagram component itself mounts/unmounts.
const trackedInteractionRecipeIds = new Set<string>();
function trackDiagramInteractionOnce(recipeId: string | undefined) {
    if (!recipeId || trackedInteractionRecipeIds.has(recipeId)) return;
    trackedInteractionRecipeIds.add(recipeId);
    track('diagram_interacted');
}

const DiagramInner = memo(forwardRef<ReactFlowDiagramHandle, ReactFlowDiagramProps>(({ graph, mode: propMode, spacing = 1, edgeStyle: propEdgeStyle = 'straight', textPos = 'bottom', isLive = false, onInteraction, onEdit, onSave, isPublic: propIsPublic, onVisibilityChange, isLoggedIn = false, onNotify, isOwner = false, iconTheme: propIconTheme = 'classic', recipeId }, ref) => {

    const iconStyle = useRecipeStore(s => s.iconStyle);
    const edgeStyle = useRecipeStore(s => s.lineStyle);
    const mode = useRecipeStore(s => s.nodeLayout);
    const backgrounds = useRecipeStore(s => s.backgrounds);
    const canvasBackground = useRecipeStore(s => s.canvasBackground);
    const iconTheme = iconStyle;
    const canvasTheme = getCanvasTheme(canvasBackground);

    // Cast hooks to avoid implicit any in callbacks
    const [nodes, setNodesRaw, onNodesChange] = useNodesState([]);
    const setNodes = setNodesRaw as React.Dispatch<React.SetStateAction<any[]>>;

    const [edges, setEdgesRaw, onEdgesChange] = useEdgesState([]);
    const setEdges = setEdgesRaw as React.Dispatch<React.SetStateAction<any[]>>;

    const { fitView, getNodes: getNodesRaw, getEdges: getEdgesRaw } = useReactFlow();
    const getNodes = getNodesRaw as () => any[];
    const getEdges = getEdgesRaw as () => any[];
    const flowWrapper = useRef<HTMLDivElement>(null);
    const simulationRef = useRef<any>(null);
    // Not yet saved (no recipeId prop) still gets one diagram_interacted per
    // mount, keyed on an id generated once for this component instance rather
    // than a shared sentinel — otherwise two different unsaved recipes edited
    // in the same tab would collide on the same key.
    const unsavedAnalyticsIdRef = useRef<string | undefined>(undefined);
    if (!unsavedAnalyticsIdRef.current) {
        unsavedAnalyticsIdRef.current = `unsaved-${Math.random().toString(36).slice(2)}`;
    }
    const analyticsRecipeId = recipeId ?? unsavedAnalyticsIdRef.current;
    const [timelineData, setTimelineData] = useState<TimelineData | null>(null);
    // e2e signal: true once the initial layout (and any fitView) has settled.
    // Tests wait on the data-testid="rf-ready" marker instead of arbitrary sleeps.
    const [layoutReady, setLayoutReady] = useState(false);
    const {
        copied,
        saved,
        isDirty,
        setIsDirty,
        isPublic,
        visibilityRef,
        getGraph,
        performSave,
        handleSave,
        handleSaveCopy,
        handleShare,
        toggleVisibility,
        toggleAnonymous,
    } = useSaveAndFork({
        graph,
        mode,
        getNodes,
        getEdges,
        isLoggedIn,
        isOwner,
        propIsPublic,
        onVisibilityChange,
        onSave,
        onNotify,
    });

    const saveButton = getSaveButtonState({ isLoggedIn, isOwner, isDirty, saved });

    const { scheduleAutosave, flushAutosave } = useAutosave({
        onSave: handleSave,
        enabled: !!isOwner && !!isLoggedIn,
    });

    // Theme Effect
    useEffect(() => {
        setNodes(nds => nds.map(n => ({
            ...n,
            data: { ...n.data, iconTheme }
        })));
    }, [iconTheme, setNodes]);

    // Wrap change handlers to track dirty state
    const onNodesChangeWrapped = useCallback((changes: any) => {
        onNodesChange(changes);
        if (changes.some((c: any) => c.type !== 'select')) {
            setIsDirty(true);
            onEdit?.();
        }
    }, [onNodesChange, onEdit]);

    const onEdgesChangeWrapped = useCallback((changes: any) => {
        onEdgesChange(changes);
        if (changes.some((c: any) => c.type !== 'select')) {
            setIsDirty(true);
            onEdit?.();
        }
    }, [onEdgesChange, onEdit]);
    
    // Drag State for Branch Rotation
    const dragRef = useRef<{
        active: boolean;
        pivot?: { x: number, y: number };
        startAngle?: number;
        startDist?: number;
        ancestors?: string[];
        initialPositions?: Record<string, { x: number, y: number }>;
    }>({ active: false });
    
    // Long Press State for Mobile Pivot
    const longPressTriggered = useRef(false);
    const selectBranch = useCallback((nodeId: string) => {
        const getAncestors = (id: string, visited = new Set<string>()): string[] => {
            if (visited.has(id)) return [];
            visited.add(id);
            // Skip the synthetic station stub: a station badge is not an
            // ancestor of its row's first step (see isSyntheticStationEdge).
            const incoming = edges.filter(
                (e: any) => e.target === id && !isSyntheticStationEdge(e),
            );
            const parents = incoming.map((e: any) => e.source);
            return [...parents, ...parents.flatMap((p: any) => getAncestors(p, visited))];
        };

        const ancestors = getAncestors(nodeId);
        const toSelect = new Set([nodeId, ...ancestors]);

        setNodes((nds) => nds.map((n) => ({
            ...n,
            selected: toSelect.has(n.id) || n.selected
        })));
    }, [edges, setNodes]);

    const setLongPress = useCallback((active: boolean, nodeId?: string) => {
        longPressTriggered.current = active;
        if (active && nodeId) {
            selectBranch(nodeId);
        }
    }, [selectBranch]);

    // Tick-off progress (#281): a count, not the array, so the toolbar re-renders
    // only when the button's enabled state can actually change.
    const completedCount = useRecipeStore(s => s.completedNodeIds.length);
    const clearCompletedNodes = useRecipeStore(s => s.clearCompletedNodes);

    // Single undo owner (issue #216): the store holds the ONE history for the
    // whole page — graph edits, drags and deletes share a single timeline.
    // The window Ctrl+Z/Ctrl+Y listener lives in app/lanes/page.tsx (so it
    // also covers the TimelineView); this component has no key handling.
    const deleteNodeWithUndo = useRecipeStore(s => s.deleteNodeWithUndo);
    const pushUndoSnapshot = useRecipeStore(s => s.pushUndoSnapshot);
    const commitNodePositions = useRecipeStore(s => s.commitNodePositions);
    const syncNodePositions = useRecipeStore(s => s.syncNodePositions);
    const storeUndo = useRecipeStore(s => s.undo);
    const storeRedo = useRecipeStore(s => s.redo);
    const canUndo = useRecipeStore(s => s.undoPast.length > 0);
    const canRedo = useRecipeStore(s => s.undoFuture.length > 0);
    const historyVersion = useRecipeStore(s => s.historyVersion);

    // Deletes go through the store: one history entry, children bridged onto
    // the deleted node's inputs, and the id recorded in pendingDeletedIds so a
    // background Firestore write cannot resurrect it before the save lands.
    // The graph change flows back into ReactFlow via the layout sync effect
    // (hasRemovedNodes → runLayout), which also rebuilds the bridged edges.
    const handleDeleteNode = useCallback((nodeId: string) => {
        deleteNodeWithUndo(nodeId);
        setIsDirty(true);
        if (isOwner) {
            scheduleAutosave();
        } else {
            onEdit?.();
        }
    }, [deleteNodeWithUndo, setIsDirty, isOwner, scheduleAutosave, onEdit]);

    // Initial Layout Calculation
    const runLayout = useCallback((preservePositions = false, fit = true) => {
        let layout;

        // Determine effective graph and preservation status based on mode
        let effectiveGraph = graph;
        let shouldUseSavedLayout = false;

        if (preservePositions) {
            // 1. Check for Independent Layout in layouts map
            if (graph.layouts && graph.layouts[mode]) {
                const layoutNodes = graph.layouts[mode];
                const nodesWithLayout = graph.nodes.map(n => {
                    const pos = layoutNodes.find(l => l.id === n.id);
                    return pos ? { ...n, x: pos.x, y: pos.y } : n;
                });
                effectiveGraph = { ...graph, nodes: nodesWithLayout };
                shouldUseSavedLayout = true;
            } 
            // 2. Fallback: If mode matches the saved 'layoutMode', use main nodes positions
            else if (graph.layoutMode === mode && graph.nodes.some(n => n.x !== undefined)) {
                 shouldUseSavedLayout = true;
            }
        }

        const canPreserve = shouldUseSavedLayout;
        const isNotation = mode === 'notation';

        if (isNotation) {
            // calculateNotationLayout is a pure from-scratch engine that never
            // reads node x/y as input, so preserving saved/dragged positions
            // must happen post-hoc on its OUTPUT — unlike the canPreserve
            // modes below, which bake x/y into the input graph (issue #268:
            // drags in notation were saved to graph.layouts.notation but never
            // applied on restore, snapping back on reload/mode-switch/undo).
            layout = calculateNotationLayout(effectiveGraph);
            const savedNotationPositions = canPreserve ? graph.layouts?.[mode] : undefined;
            if (savedNotationPositions) {
                const savedById = new Map(savedNotationPositions.map(p => [p.id, p]));
                layout = {
                    ...layout,
                    nodes: layout.nodes.map(n => {
                        // Station badges are synthetic row anchors (not in
                        // graph.nodes, not draggable) — never overlay them.
                        if ((n as any).role === 'station') return n;
                        const saved = savedById.get(n.id);
                        return saved ? { ...n, x: saved.x, y: saved.y } : n;
                    }),
                };
                // …but a badge left on the freshly-computed row pitch while its
                // row's real nodes jumped to saved ys is a DETACHED badge with a
                // diagonal stub edge climbing to it — which is what every
                // recipe saved before the dynamic row pitch would now render.
                // Put each badge back on its row's actual line.
                layout = reanchorNotationStations(layout);
            }
        } else if (canPreserve) {
            const safeMode = (['swimlanes', 'dagre', 'dagre-lr', 'timeline'].includes(mode as string)) ? (mode as LayoutMode) : 'dagre';
            layout = calculateLayout(effectiveGraph, safeMode, spacing, true);
        } else if (mode === 'repulsive') {
            layout = calculateRepulsiveCurvesLayout(effectiveGraph, spacing);
        } else {
            layout = calculateLayout(effectiveGraph, mode as LayoutMode, spacing);
        }

        const isTimeline = mode === 'timeline';

        const newNodes: Node[] = [];

        // Notation mode draws its own station badges instead of column/row
        // background bands (LaneNode assumes vertical swimlanes) — skip lane nodes.
        if (!isNotation) {
            layout.lanes.forEach(lane => {
                 newNodes.push({
                     id: lane.id,
                     type: 'lane',
                     position: { x: lane.x, y: lane.y },
                     data: { label: lane.label, color: lane.color },
                     style: {
                         width: lane.width,
                         height: lane.height,
                         zIndex: -1,
                         // In timeline mode: colored band background + no pointer events so
                         // clicking empty space deselects nodes correctly.
                         ...(isTimeline ? { backgroundColor: lane.color, pointerEvents: 'none' } : {}),
                     },
                     draggable: false,
                     selectable: false,
                     focusable: false,
                     zIndex: -1
                 });
            });
        }

        // Leaf = no incoming edge (in-degree 0: raw ingredients). Threaded into
        // node data so the node view can render it smaller via the slider (#155).
        const leafIds = getLeafNodeIds(graph);
        layout.nodes.forEach(n => {
             const originalNode = graph.nodes.find(gn => gn.id === n.id);
             const role = (n as any).role as ('leaf' | 'verb' | 'state' | 'station' | undefined);
             const nodeType = isNotation
                 ? (role === 'station' ? NOTATION_STATION_TYPE : role === 'verb' ? 'notation-verb' : 'minimal')
                 : (isTimeline ? 'timeline-node' : 'minimal');
             newNodes.push({
                 id: n.id,
                 type: nodeType,
                 position: { x: n.x, y: n.y },
                 data: {
                     ...originalNode, ...n.data,
                     ...(isTimeline ? { lineColor: (n as any).lineColor } : {}),
                     textPos: effectiveTextPos(mode as string, textPos),
                     depth: (n as any).depth,
                     isLeaf: isNotation ? role === 'leaf' : leafIds.has(n.id),
                     // "This node is being rendered inside the notation
                     // view." Three rendering decisions hang off it, all of
                     // them things the notation layout reserved space for:
                     // the label line clamp, nowrap chips, and the classic
                     // renderer (see minimal-node.tsx). Strictly gated —
                     // every other view renders exactly as before.
                     ...(isNotation ? { isNotationView: true } : {}),
                     onDelete: () => handleDeleteNode(n.id),
                     onSetLongPress: setLongPress,
                     iconTheme,
                 },
                 width: n.width,
                 height: n.height,
                 // Station badges are synthetic row anchors (not in graph.nodes):
                 // not draggable, not selectable, and not Backspace-deletable —
                 // otherwise ReactFlow's default delete handling removes them
                 // from local canvas state (looks like data loss) without going
                 // through handleDeleteNode.
                 draggable: !(isNotation && role === 'station'),
                 ...(isNotation && role === 'station'
                     ? { selectable: false, deletable: false, focusable: false }
                     : {}),
             });
        });

        // Capture timeline grid data so the background can render it.
        if (isTimeline && (layout as any).timelineData) {
            setTimelineData((layout as any).timelineData);
        } else if (!isTimeline) {
            setTimelineData(null);
        }

        const newEdges: Edge[] = layout.edges.map(e => {
            if (isNotation) {
                return {
                    id: e.id,
                    source: e.sourceId,
                    target: e.targetId,
                    type: 'notation',
                    // Carry the layout's synthetic marker into RF edge data —
                    // every consumer downstream (save path, edge walkers, the
                    // physics sim) identifies the station stub by this alone.
                    data: { kind: (e as any).kind, synthetic: (e as any).synthetic === true },
                    style: {},
                };
            }
            if (isTimeline) {
                return {
                    id: e.id,
                    source: e.sourceId,
                    target: e.targetId,
                    type: 'timeline',
                    data: { lineColor: (e as any).lineColor, kind: e.kind },
                    style: {},
                };
            }
            return {
                id: e.id,
                source: e.sourceId,
                target: e.targetId,
                type: 'floating',
                data: { variant: edgeStyle },
                style: { stroke: '#9ca3af', strokeWidth: 1.5 },
                markerEnd: {
                    type: MarkerType.ArrowClosed,
                    color: '#9ca3af',
                    width: 20,
                    height: 20,
                },
                animated: false,
            };
        });

        // Sort by Y for depth buffering (Painter's Algo)
        newNodes.sort((a, b) => a.position.y - b.position.y);

        setNodes(newNodes);
        setEdges(newEdges);
        hasInitialLayoutRef.current = true;

        // Mirror the rendered positions into the store graph (passive: no
        // history entry, no dirty flag) so history snapshots always capture
        // exactly what is on screen.
        syncNodePositions(
            mode as string,
            newNodes
                .filter((n: any) => n.type !== 'lane' && n.type !== NOTATION_STATION_TYPE)
                .map((n: any) => ({ id: n.id, x: n.position.x, y: n.position.y })),
        );

        if (fit && !canPreserve) {
            setLayoutReady(false);
            setTimeout(() => {
                // Cap zoom at 1.5 so small recipes don't fill the entire viewport.
                // If the view zooms in too far, the pane centre lands on a node and
                // e2e pan drags register as node-drag instead of canvas-pan.
                fitView(fitOptionsFor(mode as string));
                // fitView has settled — surface the e2e readiness signal.
                setLayoutReady(true);
            }, 50);
        } else {
            // No fitView to wait on; nodes are positioned synchronously.
            setLayoutReady(true);
        }

    }, [graph, mode, spacing, setNodes, setEdges, fitView, handleDeleteNode, edgeStyle, syncNodePositions]);

    const prevMode = useRef(mode);
    const prevSpacing = useRef(spacing);
    // Set to true after the first runLayout completes. Once nodes are laid out,
    // subsequent graph updates (snapshots, saves) must NOT re-run layout — that
    // would reset positions the user has moved. Reset when the recipe changes
    // (component unmounts because {graph ? <Diagram/> : null} swaps it out).
    const hasInitialLayoutRef = useRef(false);

    // Tracks the serialized content of graph.layouts[mode] from the last effect
    // run. When the saved layout data arrives for the first time (or changes) after
    // the initial dagre render, we must call runLayout(true) so those positions are
    // applied — the "two-snapshot" bug: first Firestore snapshot has no layouts,
    // second snapshot has them, but by then hasInitialLayoutRef is already true.
    const prevLayoutsKeyRef = useRef<string | null | undefined>(undefined);

    // True while a mode/spacing switch is scheduled but its 5ms layout timer
    // hasn't fired yet (re-renders can cancel + reschedule it repeatedly).
    const pendingLayoutSwitchRef = useRef(false);

    // Layout Effect
    useEffect(() => {
        if (isLive) return; // Skip static layout if physics is running

        const modeChanged = prevMode.current !== mode;
        const spacingChanged = prevSpacing.current !== spacing;

        if (modeChanged || spacingChanged) {
            setIsDirty(true);
            pendingLayoutSwitchRef.current = true;

            // Yield to main thread for UI updates.
            // On mode change: restore saved positions if available. On spacing-only change: always re-run fresh layout.
            //
            // prevMode/prevSpacing are committed INSIDE the timeout, when the
            // layout actually runs. They used to be set eagerly before it —
            // so when an unrelated re-render (icon snapshot, text sync…)
            // cancelled this timer during the 5ms window, the next effect run
            // saw modeChanged === false and the layout switch was silently
            // lost forever (the "select says Timeline but canvas didn't
            // change until you hit reset" bug). Committing them on fire means
            // a cancelled switch simply reschedules on the next run.
            const hasSavedPositions = modeChanged && !!(graph.layouts?.[mode]);
            const shouldFit = modeChanged;
            const timer = setTimeout(() => {
                pendingLayoutSwitchRef.current = false;
                prevMode.current = mode;
                prevSpacing.current = spacing;
                runLayout(hasSavedPositions, shouldFit);
            }, 5);
            return () => clearTimeout(timer);
        }

        // Detect whether saved layout data has arrived or changed since the last
        // render. We compare by JSON content (not by reference) so that Firebase
        // re-sending identical data does not trigger an unnecessary re-layout.
        const currentLayoutsKey: string | null =
            graph.layouts?.[mode] ? JSON.stringify(graph.layouts[mode]) : null;
        // Fires once when saved layout data arrives after the initial dagre render ran
        // without it (two-snapshot scenario). The key comparison already prevents
        // re-firing on unchanged data, so we don't need an !isDirty guard here.
        // Notation is included since runLayout overlays layouts['notation'] onto
        // the computed spine layout (issue #268): the first-autosave echo now
        // re-applies exactly the just-dragged positions (a visual no-op) instead
        // of snapping back, and the two-snapshot restore works like other modes.
        const layoutsJustArrived =
            hasInitialLayoutRef.current &&
            currentLayoutsKey !== null &&
            prevLayoutsKeyRef.current === null;
        prevLayoutsKeyRef.current = currentLayoutsKey;

        if (layoutsJustArrived) {
            runLayout(true);
            return;
        }


        if (isDirty || hasInitialLayoutRef.current) {
            // Detect full regeneration: if none of the incoming graph node IDs match
            // current ReactFlow nodes, the recipe was re-parsed with all-new IDs.
            // In that case, do a fresh layout rather than a no-op metadata patch.
            // Synthetic notation station badges are RF-only (never in graph.nodes):
            // they must be excluded here or hasRemovedNodes is permanently true in
            // notation mode, re-running the from-scratch layout on EVERY graph tick
            // (autosave echoes, icon writes …) and snapping user drags back.
            const currentRFNodeIds = new Set(
                getNodes()
                    .filter((n: any) => n.type !== 'lane' && n.type !== NOTATION_STATION_TYPE)
                    .map((n: any) => n.id)
            );
            const incomingIds = graph.nodes.map(n => n.id);
            // Guard: if rfNodes is empty but hasInitialLayoutRef is true, runLayout
            // just executed synchronously and the RF store hasn't rendered yet.
            // Treat as overlap to avoid incorrectly running a fresh dagre layout.
            const hasOverlap =
                currentRFNodeIds.size === 0 ||
                incomingIds.length === 0 ||
                incomingIds.some(id => currentRFNodeIds.has(id));
            if (!hasOverlap) {
                hasInitialLayoutRef.current = false;
                runLayout(false, true);
                return;
            }

            // Detect nodes added or removed since the last layout (e.g. AI adjustment).
            // New nodes from applyPatch are in graph.nodes but have no RF node yet;
            // removed nodes are in RF but gone from graph. Either case requires a fresh
            // layout pass so the new/removed nodes are actually rendered.
            const graphNodeIds = new Set(graph.nodes.map(n => n.id));
            const hasNewNodes = graph.nodes.some(n => !currentRFNodeIds.has(n.id));
            const hasRemovedNodes = [...currentRFNodeIds].some(id => !graphNodeIds.has(id));
            if (hasNewNodes || hasRemovedNodes) {
                runLayout(true, false);
                return;
            }

            // Once the initial layout has run (or while dirty), ONLY apply metadata updates
            // (icons, text, serves) from DB to EXISTING nodes.
            // We DO NOT restore deleted nodes or move nodes based on DB — that would reset
            // positions the user has moved.
            // This prevents moving an icon in one tab from affecting another tab.
            setNodes(currentNodes => {
                let changed = false;
                const newNodes = currentNodes.map(n => {
                     const dbNode = graph.nodes.find(dn => dn.id === n.id);
                     if (dbNode) {
                         // Check for Icon Update
                         const dbUrl = getNodeIconUrl(dbNode);
                         const currentUrl = getNodeIconUrl(n.data);

                         // Always sync text/serves/baseServes from DB prop even if dirty,
                         // so that top-level scaling (serves) and background updates (icons) work.
                         const baseData = {
                             ...n.data,
                             text: dbNode.text,
                             serves: graph.serves,
                             baseServes: graph.baseServes
                         };

                         // Copy shortlist from DB when the icon changed (forge result arrived).
                         const newData = preserveNodeShortlist(baseData, dbNode);

                         // If serves or text changed, mark as changed to trigger re-render
                         if (n.data.serves !== graph.serves || n.data.text !== dbNode.text || newData !== baseData) changed = true;

                         return {
                             ...n,
                             data: newData
                         };
                     }
                     return n;
                });
                // TODO: Show a visual to confirm this happens when we think it should.
                return changed ? newNodes : currentNodes;
            });
        } else {
            runLayout(true);
        }
    }, [graph, mode, spacing, runLayout, isDirty, setNodes]);

    // Undo/redo restored a different graph: re-apply its positions to the
    // canvas (a position-only change is deliberately ignored by the sync
    // effect above — it must not move nodes on ordinary background writes)
    // and persist the restored state so Firestore converges on what the user
    // now sees.
    const prevHistoryVersionRef = useRef(historyVersion);
    useEffect(() => {
        if (prevHistoryVersionRef.current === historyVersion) return;
        prevHistoryVersionRef.current = historyVersion;
        runLayout(true, false);
        if (isOwner) scheduleAutosave();
    }, [historyVersion, runLayout, isOwner, scheduleAutosave]);

    // Text Position Update Effect. Notation pins the label below the icon —
    // see effectiveTextPos (a 'left'/'right' wrapper is 40-60px wider and
    // breaks the layout's spacing and the edge anchors).
    //
    // Depending on `mode` means this now fires on every layout switch, so it
    // returns the SAME array when nothing actually changes (the same guard the
    // graph-sync effect above uses). Otherwise every mode switch handed
    // ReactFlow a fresh object for every node and re-rendered the whole canvas
    // for nothing.
    useEffect(() => {
        const wanted = effectiveTextPos(mode as string, textPos);
        setNodes(nds => {
            if (nds.every(n => n.data?.textPos === wanted)) return nds;
            return nds.map(n => (
                n.data?.textPos === wanted ? n : { ...n, data: { ...n.data, textPos: wanted } }
            ));
        });
    }, [textPos, mode, setNodes]);

    // Edge Style Update Effect
    useEffect(() => {
        setEdges(eds => eds.map(e => ({
            ...e,
            data: { ...e.data, variant: edgeStyle }
        })));
    }, [edgeStyle, setEdges]);

    const prevIsLiveRef = useRef(isLive);

    // Live Force Simulation
    useEffect(() => {
        const wasLive = prevIsLiveRef.current;
        prevIsLiveRef.current = isLive;

        if (!isLive) {
            if (simulationRef.current) simulationRef.current.stop();
            if (wasLive) {
                setIsDirty(true);
                // Physics moved nodes only in ReactFlow state — mirror the
                // post-simulation positions into the store, otherwise the next
                // history snapshot (e.g. a drag commit) captures the stale
                // pre-physics graph and one undo reverts the whole simulation.
                syncNodePositions(
                    mode as string,
                    getNodes()
                        .filter((n: any) => n.type !== 'lane' && n.type !== NOTATION_STATION_TYPE)
                        .map((n: any) => ({ id: n.id, x: n.position.x, y: n.position.y })),
                );
            }
            return;
        }

        // Push a history entry on entering physics mode: the store graph
        // mirrors the pre-physics positions (synced by the last layout pass),
        // so one undo restores the pre-physics arrangement.
        if (!wasLive) pushUndoSnapshot();

        // Station badges are fixed row anchors, not bodies: the sim would
        // scatter them and the stop-sync below (which skips notation-station on
        // purpose, since they are not graph nodes) has nothing to restore them
        // with. Their stub edges leave with them, or forceLink would resolve a
        // source id that is no longer a simulation node.
        const d3Nodes = nodes
            .filter((n: any) => n.type !== 'lane' && n.type !== NOTATION_STATION_TYPE)
            .map((n: any) => ({
                id: n.id,
                x: n.position.x,
                y: n.position.y,
                width: n.width || 100,
                depth: n.data.depth || 0
            }));

        const d3Links = edges
            .filter((e: any) => !isSyntheticStationEdge(e))
            .map((e: any) => ({ source: e.source, target: e.target }));

        const sim = forceSimulation(d3Nodes as any)
            .force("link", forceLink(d3Links).id((d: any) => d.id).distance(100 * spacing))
            .force("charge", forceManyBody().strength(-300))
            .force("collide", forceCollide().radius((d: any) => (d.width/2) + 20))
            .force("y", forceY((d: any) => d.depth * -150 * spacing).strength(0.1)) 
            .force("x", forceX().strength(0.01))
            .alphaDecay(0) 
            .velocityDecay(0.95) 
            .on('tick', () => {
                 setNodes((nds: any[]) => nds.map((n: any) => {
                     const d3n = d3Nodes.find((dn: any) => dn.id === n.id);
                     if (d3n) {
                         return { ...n, position: { x: d3n.x, y: d3n.y } };
                     }
                     return n;
                 }));
            });
        
        simulationRef.current = sim;

        return () => { sim.stop(); };
    }, [isLive, spacing, graph]); 

    const downloadImage = async () => {
        if (!flowWrapper.current) return;
        
        const download = (dataUrl: string) => {
            const link = document.createElement('a');
            link.download = `recipe-lanes-${mode}.png`;
            link.href = dataUrl;
            link.click();
        };

        try {
            const dataUrl = await toPng(flowWrapper.current, {
                backgroundColor: canvasTheme.exportBackground,
                style: { width: 'auto', height: 'auto', transform: 'none' },
                cacheBust: true,
                skipFonts: true,
                pixelRatio: 2
            });
            download(dataUrl);
        } catch (err) {
            console.warn("Download failed, retrying with minimal options...", err);
            try {
                const dataUrl = await toPng(flowWrapper.current, {
                    backgroundColor: canvasTheme.exportBackground,
                    style: { width: 'auto', height: 'auto', transform: 'none' },
                    pixelRatio: 2,
                    skipFonts: true,
                    fontEmbedCSS: ''
                });
                download(dataUrl);
            } catch (e2) {
                console.error("Download failed again:", e2);
                onNotify?.("Download failed. Check console/CORS.");
            }
        }
    };

    const handleReset = () => {
        // Undoable: push the current graph (positions mirrored by the last
        // layout pass) before recomputing from scratch.
        pushUndoSnapshot();
        setIsDirty(true);
        runLayout(false);
    };

    useImperativeHandle(ref, () => ({
        resetLayout: handleReset,
        toggleVisibility: toggleVisibility,
        toggleAnonymous: toggleAnonymous,
        getGraph: getGraph
    }));

    const onNodeClick = (event: React.MouseEvent, node: Node) => {
        onInteraction?.();
        trackDiagramInteractionOnce(analyticsRecipeId);
        // No history entry here — selection isn't an undoable graph change
        // (the old per-click RF snapshot polluted the history with no-ops).
        selectBranch(node.id);
    };

    const onNodeContextMenu = (event: React.MouseEvent, node: Node) => {
        onInteraction?.();
        event.preventDefault();
        selectBranch(node.id);
    };

    const initPivotDrag = (node: Node) => {
        longPressTriggered.current = false; // Reset immediately
        const allNodes = getNodes();
        // The synthetic station stub is not a real parent/child relationship:
        // rotating a branch through it would move the (undraggable, un-restored)
        // station badge off its row.
        const outgoing = edges.find(
            (e: any) => e.source === node.id && !isSyntheticStationEdge(e),
        );
        const child = outgoing ? allNodes.find((n: any) => n.id === outgoing.target) : null;

        if (child) {
            // Same walk as selectBranch, and it must apply the same synthetic
            // filter: a pivot rotation that pulled the station badge in would
            // move a node that is not draggable and never gets restored.
            const getAncestors = (id: string, visited = new Set<string>()): string[] => {
                if (visited.has(id)) return [];
                visited.add(id);
                const incoming = edges.filter(
                    (e: any) => e.target === id && !isSyntheticStationEdge(e),
                );
                const parents = incoming.map((e: any) => e.source);
                return [...parents, ...parents.flatMap((p: any) => getAncestors(p, visited))];
            };

            const ancestors = getAncestors(node.id);

            const initialPositions: Record<string, { x: number, y: number }> = {};
            const candidates = [node.id, ...ancestors];
            candidates.forEach((id: any) => {
                const n = allNodes.find((an: any) => an.id === id);
                if (n) initialPositions[id] = { ...n.position };
            });

            const dx = node.position.x - child.position.x;
            const dy = node.position.y - child.position.y;
            
            dragRef.current = {
                active: true,
                pivot: { x: child.position.x, y: child.position.y },
                startAngle: Math.atan2(dy, dx),
                startDist: Math.sqrt(dx*dx + dy*dy),
                ancestors,
                initialPositions
            };
        }
    };

    const onNodeDragStart = (event: React.MouseEvent, node: Node) => {
        onInteraction?.();
        // History is pushed at drag STOP (commitNodePositions captures the
        // pre-move store graph) — no snapshot needed at drag start.
        if (event.shiftKey || longPressTriggered.current) {
            initPivotDrag(node);
        }
    };

    const onNodeDrag = (event: React.MouseEvent, node: Node) => {
        if (longPressTriggered.current && !dragRef.current.active) {
            initPivotDrag(node);
        }

        if (dragRef.current.active && dragRef.current.pivot && dragRef.current.initialPositions) {
            const { pivot, startAngle, startDist, ancestors, initialPositions } = dragRef.current;
            
            const dx = node.position.x - pivot.x;
            const dy = node.position.y - pivot.y;
            const currAngle = Math.atan2(dy, dx);
            const currDist = Math.sqrt(dx*dx + dy*dy);
            
            const rotation = currAngle - (startAngle || 0);
            const scale = (startDist && startDist > 0) ? currDist / startDist : 1;

            if (ancestors) {
                setNodes(nds => nds.map(n => {
                    if (ancestors.includes(n.id)) {
                        const initPos = initialPositions[n.id];
                        const vx = initPos.x - pivot.x;
                        const vy = initPos.y - pivot.y;
                        
                        const rx = vx * Math.cos(rotation) - vy * Math.sin(rotation);
                        const ry = vx * Math.sin(rotation) + vy * Math.cos(rotation);
                        
                        return {
                            ...n,
                            position: {
                                x: pivot.x + rx * scale,
                                y: pivot.y + ry * scale
                            }
                        };
                    }
                    return n;
                }));
            }
        }
    };

    const updateLaneBounds = useCallback(() => {
        setNodes(nds => {
            const laneNodes = nds.filter(n => n.type === 'lane');
            if (laneNodes.length === 0) return nds;

            const contentNodes = nds.filter(n => n.type !== 'lane');
            let changed = false;

            const newNodes = nds.map(n => {
                if (n.type === 'lane') {
                    const children = contentNodes.filter(c => c.data.laneId === n.id);
                    if (children.length > 0) {
                        let maxY = 0;
                        children.forEach(c => {
                            const bottom = c.position.y + (c.height || 100);
                            if (bottom > maxY) maxY = bottom;
                        });
                        const newHeight = Math.max(maxY + 50, 600);
                        if (n.style?.height !== newHeight) {
                            changed = true;
                            return { ...n, style: { ...n.style, height: newHeight } };
                        }
                    }
                }
                return n;
            });
            return changed ? newNodes : nds;
        });
    }, [setNodes]);

    const onNodeDragStop = () => {
        dragRef.current = { active: false };
        trackDiagramInteractionOnce(analyticsRecipeId);
        updateLaneBounds();
        // One history entry per drag: commitNodePositions pushes the pre-move
        // graph, then records every real node's rendered position (so
        // multi-node pivot drags are captured as a single undoable step).
        commitNodePositions(
            mode as string,
            getNodes()
                .filter((n: any) => n.type !== 'lane' && n.type !== NOTATION_STATION_TYPE)
                .map((n: any) => ({ id: n.id, x: n.position.x, y: n.position.y })),
        );
        if (isOwner) {
            scheduleAutosave();
        } else {
            onEdit?.();
        }
    };

    return (
        <div className="w-full h-full touch-none" ref={flowWrapper} style={{ backgroundColor: canvasTheme.surface }}>
            {layoutReady && <div data-testid="rf-ready" style={{ display: 'none' }} />}
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChangeWrapped}
                onEdgesChange={onEdgesChangeWrapped}
                onNodeClick={onNodeClick}
                onNodeContextMenu={onNodeContextMenu}
                onNodeDragStart={onNodeDragStart}
                onNodeDrag={onNodeDrag}
                onNodeDragStop={onNodeDragStop}
                onPaneClick={() => onInteraction?.()}
                onMoveStart={() => onInteraction?.()}
                nodeTypes={INITIAL_NODE_TYPES}
                edgeTypes={INITIAL_EDGE_TYPES}
                fitView
                fitViewOptions={fitOptionsFor(mode as string)}
                minZoom={0.1}
                maxZoom={4}
                nodeDragThreshold={10} // Prevent accidental drags, allow long press jitter
                defaultEdgeOptions={{ type: 'floating' }}
                onlyRenderVisibleElements={false}
                multiSelectionKeyCode={['Shift']}
            >
                {timelineData
                    ? <TimelineBackground data={timelineData} />
                    : <Background color={canvasTheme.pattern} gap={20} />
                }
                {/* Bottom-LEFT is where the Legend panel lives (app/lanes/page.tsx,
                    z-30), which painted straight over the default Controls
                    position: fitView/zoom were unclickable at the default
                    viewport — clicks landed on the Legend. */}
                {/* fitViewOptions on <ReactFlow> only governs the fit-on-init;
                    the Controls' own fit button calls fitView() with ITS prop,
                    so notation's padding has to be handed to both or clicking
                    the button silently refits at the default 0.1. */}
                <Controls showInteractive={false} position="bottom-right" fitViewOptions={fitOptionsFor(mode as string)} />
                <Panel position="top-right" className="flex gap-2">
                    <div className="flex gap-1 mr-2 border-r border-zinc-200 pr-2">
                        <button
                            onClick={storeUndo}
                            disabled={!canUndo}
                            className="bg-white p-2 rounded shadow-md border border-zinc-200 hover:bg-zinc-50 text-zinc-600 disabled:opacity-50"
                            title="Undo (Ctrl+Z)"
                        >
                            <Undo className="w-4 h-4" />
                        </button>
                        <button
                            onClick={storeRedo}
                            disabled={!canRedo}
                            className="bg-white p-2 rounded shadow-md border border-zinc-200 hover:bg-zinc-50 text-zinc-600 disabled:opacity-50"
                            title="Redo (Ctrl+Shift+Z)"
                        >
                            <Redo className="w-4 h-4" />
                        </button>
                        {/* Clear the cook's tick marks (#281). Activates as soon as
                            anything is ticked; disabled otherwise, like undo/redo. */}
                        <button
                            onClick={clearCompletedNodes}
                            disabled={completedCount === 0}
                            className="bg-white p-2 rounded shadow-md border border-zinc-200 hover:bg-zinc-50 text-zinc-600 disabled:opacity-50"
                            title="Clear all ticks"
                            data-testid="clear-completed-nodes"
                        >
                            <ListChecks className="w-4 h-4" />
                        </button>
                    </div>

                    <div className="relative group">
                        {/* Save sits on top of the stack (relative z-10, opaque bg) so the
                            Copy button can tuck directly behind it when at rest. */}
                        <button
                            onClick={handleSave}
                            disabled={!saveButton.enabled}
                            className={`relative z-10 flex items-center gap-1.5 p-2 rounded shadow-md border border-zinc-200 transition-colors ${saved ? 'bg-green-50 text-green-600 border-green-200' : saveButton.enabled ? 'bg-blue-50 text-blue-600 border-blue-200 hover:bg-blue-100' : 'bg-white text-zinc-400'}`}
                            title={saveButton.label}
                        >
                            {saved ? <Check className="w-4 h-4" /> : <Save className="w-4 h-4" />}
                            {saveButton.isCopy && <span className="text-xs font-bold hidden sm:inline">{saveButton.label}</span>}
                        </button>
                        {/* Save-a-copy (issue #239): a same-sized square Copy icon below the
                            Save button (top-0, z-0).
                            - Mobile (base): there's no hover, so it's PERMANENTLY out and
                              tappable — translated fully below Save with pointer-events on.
                            - Desktop (sm:): it stacks *behind* Save, peeking a few px at rest
                              (sm:translate-y-1.5) for discoverability, and slides fully down
                              out from behind Save on hover/focus. The ::before strip bridges
                              the reveal gap so the hover stays live. */}
                        {isLoggedIn && (
                            <button
                                onClick={handleSaveCopy}
                                className="absolute left-0 top-0 z-0 translate-y-[calc(100%+0.25rem)] pointer-events-auto sm:translate-y-1.5 sm:pointer-events-none p-2 rounded shadow-md border border-zinc-200 bg-white text-zinc-600 transition-all duration-200 ease-out hover:bg-zinc-50 sm:group-hover:translate-y-[calc(100%+0.25rem)] sm:group-hover:pointer-events-auto sm:focus-visible:translate-y-[calc(100%+0.25rem)] sm:focus-visible:pointer-events-auto before:absolute before:-top-1 before:left-0 before:h-1 before:w-full before:content-['']"
                                title="Save a copy"
                                aria-label="Save a copy"
                            >
                                <Copy className="w-4 h-4" />
                            </button>
                        )}
                    </div>

                     <button
                        onClick={handleShare} 
                        className={`p-2 rounded shadow-md border border-zinc-200 transition-colors ${copied ? 'bg-green-50 text-green-600 border-green-200' : 'bg-white text-zinc-600 hover:bg-zinc-50'}`}
                        title={copied ? "Copied!" : "Save & Copy Link"}
                    >
                        {copied ? <Check className="w-4 h-4" /> : <Share2 className="w-4 h-4" />}
                    </button>
                    <button 
                        onClick={downloadImage} 
                        className="bg-white p-2 rounded shadow-md border border-zinc-200 hover:bg-zinc-50 text-zinc-600"
                        title="Download PNG"
                    >
                        <Download className="w-4 h-4" />
                    </button>
                </Panel>
            </ReactFlow>
        </div>
    );
}));
DiagramInner.displayName = "DiagramInner";

const ReactFlowDiagram = forwardRef<ReactFlowDiagramHandle, ReactFlowDiagramProps>((props, ref) => (
    <DiagramInner {...props} ref={ref} />
));
ReactFlowDiagram.displayName = "ReactFlowDiagram";

export default ReactFlowDiagram;